export module MathUtils {

  /**
   * Default precision when comparing floating-point numbers.
   */
  export const PRECISION = 1e-4;

  /**
   * Check the equality of two numbers against the requested precision.
   */
  export function fuzzyEquals(lhs: number, rhs: number, precision: number = MathUtils.PRECISION): boolean {
    return Math.abs(lhs - rhs) < precision;
  }

  /**
   * Check the nullity of a number against the requested precision.
   */
  export function fuzzyNull(value: number, precision?: number): boolean {
    return MathUtils.fuzzyEquals(value, 0, precision);
  }

  /**
   * Rounds a number to the requested number of decimals.
   */
  export function roundDecimal(value: number, numberOfDecimals: number = 0): number {
    const factor = Math.pow(10, numberOfDecimals);
    return Math.round(value * factor) / factor;
  }

  /**
   * Rounds a number to the requested precision.
   * @param value Value to round.
   * @param step Precision of the rounding.
   * @return Rounded value.
   */
  export function roundStep(value: number, step: number): number {
    if (MathUtils.fuzzyNull(step)) {
      return 0;
    }
    return Math.round(value / step) * step;
  }

  /**
   * Rounds down a number to the requested precision.
   * @param value Value to round.
   * @param step Precision of the rounding.
   * @return Rounded value.
   */
  export function floorStep(value: number, step: number): number {
    if (MathUtils.fuzzyNull(step)) {
      return 0;
    }
    return Math.floor(value / step) * step;
  }

  /**
   * Rounds up a number to the requested precision.
   * @param value Value to round.
   * @param step Precision of the rounding.
   * @return Rounded value.
   */
  export function ceilStep(value: number, step: number): number {
    if (MathUtils.fuzzyNull(step)) {
      return 0;
    }
    return Math.ceil(value / step) * step;
  }

  /**
   * Serializes a number to the requested precision.
   * @param value Value to round.
   * @param step Precision of the rounding.
   * @param positivePrefix Prefix used when the rounded value is positive.
   * @param zeroPrefix Prefix used when the rounded value is null.
   * @param negativePrefix Prefix used when the rounded value is negative.
   * @return Serialized value.
   */
  export function toFixedStep(
    value: number,
    step: number,
    positivePrefix: string = '',
    zeroPrefix: string = '',
    negativePrefix: string = '-',
  ): string {
    const roundedValue = MathUtils.roundStep(value, step);
    let prefix = '';
    switch (true) {
      case roundedValue > 0:
        prefix = positivePrefix;
        break;
      case roundedValue === 0:
        prefix = zeroPrefix;
        break;
      case roundedValue < 0:
        prefix = negativePrefix;
        break;
    }
    return prefix + Math.abs(roundedValue).toFixed(MathUtils.numberOfDecimals(step));
  }

  /**
   * Computes the number of decimals of a number.
   */
  export function numberOfDecimals(value: number): number {
    if (Math.floor(value) === value) {
      return 0;
    }
    return value.toString().split('.')[1].length || 0;
  }

  /**
   * Computes the mean value of a set of numbers.
   */
  export function mean(data: Array<number>): number | null {
    if (data.length === 0) {
      return null;
    }
    return data.reduce(
      (accumulated: number, current: number): number => {
        return accumulated + current;
      }) / data.length;
  }

  /**
   * Computes the standard deviation of a set of numbers.
   */
  export function standardDeviation(data: Array<number>): number | null {
    if (data.length < 2) {
      return null;
    }
    const meanValue: number = MathUtils.mean(data)!;
    return Math.sqrt(
      data.map(value => (value - meanValue) ** 2)
        .reduce((acc, val) => acc + val)
      / data.length,
    );
  }

  /**
   * Creates a set of number uniformly spaced between begin and end.
   */
  export function createLinearArray(begin: number, end: number, length: number): Array<number> {
    const array: Array<number> = [];
    const step: number = MathUtils.fuzzyEquals(length, 1) ? 0 : (end - begin) / (length - 1);
    for (let i = 0; i < length; i++) {
      array.push(begin + (step * i));
    }
    return array;
  }

}
